import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "../../Components/Common/LoadingStates";
import {
  getRewardsWallets,
  getRewardsWallet,
  getRewardsReferrals,
  getRewardsTopReferrers,
  getRewardsReferralStats,
} from "../../helpers/fakebackend_helper";
import {
  card, btn, btnGhost, td, COLORS,
  shortAddr, fmtNum, fmtDate, Muted, Stat, StatGrid, Pagination, SearchBar, Table, pageMetaOf,
} from "./shared";
import { apiError, listOf } from "./rewardsUtil";

const yesNo = (v) => (v ? "Yes" : "-");

// ── Wallets tab (list, then detail drill-down) ───────────────────────────────
export function WalletsTab({ flash }) {
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({ page: 1, total_pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const res = await getRewardsWallets({ search: query || undefined, page, page_size: 25 });
      setRows(res?.wallets || []);
      setMeta(pageMetaOf(res));
    } catch (err) {
      flash("error", apiError(err, "Failed to load wallets"));
      setRows([]);
    }
  }, [flash, page, query]);
  useEffect(() => { load(); }, [load]);

  const submit = (e) => { e.preventDefault(); setPage(1); setQuery(search.trim().toLowerCase()); };
  const back = useCallback(() => setDetail(null), []);

  if (detail) return <WalletDetail address={detail} onBack={back} flash={flash} />;

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <h5 style={{ margin: 0 }}>Wallets</h5>
        <SearchBar value={search} onChange={setSearch} onSubmit={submit} onRefresh={load} placeholder="Search address…" />
      </div>
      {rows === null ? <Spinner /> : rows.length === 0 ? (
        <Muted>No wallets found.</Muted>
      ) : (
        <Table head={["Wallet", "Pending", "Threshold", "Progress", "Tier", "Boost", "Lifetime Vol ($)", "Claimable", ""]}>
          {rows.map((r) => (
            <tr key={r.wallet_address}>
              <td style={td} title={r.wallet_address}>{shortAddr(r.wallet_address)}</td>
              <td style={td}>{fmtNum(r.pending_points)}</td>
              <td style={td}>{fmtNum(r.threshold_points)}</td>
              <td style={td}>{Number(r.progress_percent).toFixed(1)}%</td>
              <td style={td}>{r.tier}</td>
              <td style={td}>+{r.volume_bonus}%</td>
              <td style={td}>{fmtNum(r.lifetime_volume_usd)}</td>
              <td style={td}>{yesNo(r.is_claimable)}</td>
              <td style={td}><button style={btnGhost} onClick={() => setDetail(r.wallet_address)}>View</button></td>
            </tr>
          ))}
        </Table>
      )}
      <Pagination page={meta.page} totalPages={meta.total_pages} total={meta.total} onPage={setPage} />
    </div>
  );
}

// ── Wallet detail ─────────────────────────────────────────────────────────────
function WalletDetail({ address, onBack, flash }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setData(null);
    try {
      setData(await getRewardsWallet(address));
    } catch (err) {
      flash("error", apiError(err, "Failed to load wallet"));
      onBack();
    }
  }, [address, flash, onBack]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div style={card}><Spinner /></div>;
  const s = data.state;
  const simpleTable = (rowsArr, head, render, empty) =>
    !rowsArr || rowsArr.length === 0 ? <Muted>{empty}</Muted> : <Table head={head}>{rowsArr.map(render)}</Table>;

  return (
    <div>
      <div style={{ ...card, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 600, fontFamily: "monospace", wordBreak: "break-all" }}>{address}</div>
          <div style={{ color: "var(--bl-muted, #8b93a7)", fontSize: 13 }}>
            {data.exists ? "Has reward state" : "No reward state yet"} · referral code: {data.referral_code || "-"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={btnGhost} onClick={load}>Refresh</button>
          <button style={btn} onClick={onBack}>Back</button>
        </div>
      </div>

      {s && (
        <div style={card}>
          <h5 style={{ marginTop: 0 }}>State</h5>
          <StatGrid min={140}>
            <Stat label="Pending points" value={fmtNum(s.pending_points)} />
            <Stat label="Threshold" value={fmtNum(s.threshold_points)} />
            <Stat label="Progress" value={`${Number(s.progress_percent).toFixed(1)}%`} />
            <Stat label="Claimable" value={s.is_claimable ? "Yes" : "No"} />
            <Stat label="Tier" value={s.tier} />
            <Stat label="Volume bonus" value={`+${s.volume_bonus}%`} />
            <Stat label="Lifetime vol" value={`$${fmtNum(s.lifetime_volume_usd)}`} />
          </StatGrid>
        </div>
      )}

      <div style={card}>
        <h5 style={{ marginTop: 0 }}>Points by source (lifetime)</h5>
        {simpleTable(
          data.points_by_source, ["Source", "Points"],
          (p) => <tr key={p.source}><td style={td}>{p.source}</td><td style={td}>{fmtNum(p.points)}</td></tr>,
          "No ledger entries."
        )}
      </div>

      <div style={card}>
        <h5 style={{ marginTop: 0 }}>Referral</h5>
        <div style={{ marginBottom: 10 }}>
          <strong>Referred by: </strong>
          {data.referred_by ? (
            <span title={data.referred_by.referrer_wallet} style={{ fontFamily: "monospace" }}>
              {shortAddr(data.referred_by.referrer_wallet)} {data.referred_by.active ? "(active)" : "(expired)"}
            </span>
          ) : <Muted>none</Muted>}
        </div>
        <div style={{ marginBottom: 6 }}><strong>Referred others ({data.referrals?.count || 0}):</strong></div>
        {simpleTable(
          data.referrals?.list, ["Referee", "Since", "Until", "Active"],
          (r) => (
            <tr key={r.referee_wallet}>
              <td style={td} title={r.referee_wallet}>{shortAddr(r.referee_wallet)}</td>
              <td style={td}>{fmtDate(r.start_at)}</td>
              <td style={td}>{fmtDate(r.end_at)}</td>
              <td style={td}>{yesNo(r.active)}</td>
            </tr>
          ),
          "Has not referred anyone."
        )}
      </div>

      {data.linked_wallets?.length > 1 && (
        <div style={card}>
          <h5 style={{ marginTop: 0 }}>Linked wallets ({data.linked_wallets.length})</h5>
          {simpleTable(
            data.linked_wallets, ["Wallet", "Chain", "Primary ETH"],
            (l) => (
              <tr key={l.wallet_address}>
                <td style={td} title={l.wallet_address}>{shortAddr(l.wallet_address)}</td>
                <td style={td}>{l.chain}</td>
                <td style={td}>{yesNo(l.is_primary_eth)}</td>
              </tr>
            ),
            "No linked wallets."
          )}
        </div>
      )}

      <div style={card}>
        <h5 style={{ marginTop: 0 }}>Recent ledger</h5>
        {simpleTable(
          data.ledger, ["When", "Type", "Source", "Points change"],
          (l) => (
            <tr key={l.ledger_id}>
              <td style={td}>{fmtDate(l.created_at)}</td>
              <td style={td}>{l.entry_type}</td>
              <td style={td}>{l.source}</td>
              <td style={{ ...td, color: l.points_delta < 0 ? COLORS.error : COLORS.success }}>
                {l.points_delta > 0 ? "+" : ""}{fmtNum(l.points_delta)}
              </td>
            </tr>
          ),
          "No ledger entries."
        )}
      </div>

      <div style={card}>
        <h5 style={{ marginTop: 0 }}>Claims</h5>
        {simpleTable(
          data.claims, ["Claim", "Points", "Status", "Tx", "When"],
          (c) => (
            <tr key={c.claim_id}>
              <td style={td}>#{c.claim_id}</td>
              <td style={td}>{fmtNum(c.points_debited)}</td>
              <td style={td}>{c.status}</td>
              <td style={td} title={c.payout_tx_hash || ""}>{c.payout_tx_hash ? shortAddr(c.payout_tx_hash) : "-"}</td>
              <td style={td}>{fmtDate(c.created_at)}</td>
            </tr>
          ),
          "No claims."
        )}
      </div>
    </div>
  );
}

// ── Referral KPI strip ───────────────────────────────────────────────────────
function ReferralStatsStrip({ flash }) {
  const [stats, setStats] = useState(undefined);
  useEffect(() => {
    let live = true;
    getRewardsReferralStats()
      .then((r) => { if (live) setStats(r || null); })
      .catch((err) => { if (live) { setStats(null); flash("error", apiError(err, "Failed to load referral stats")); } });
    return () => { live = false; };
  }, [flash]);

  return (
    <div style={card}>
      <h5 style={{ marginTop: 0 }}>Referral program</h5>
      {stats === undefined ? <Spinner /> : stats === null ? <Muted>Referral stats are unavailable.</Muted> : (
        <StatGrid>
          <Stat label="Referees" value={fmtNum(stats.referees_total)} />
          <Stat label="Active referees" value={fmtNum(stats.referees_active)} />
          <Stat label="Referrers" value={fmtNum(stats.referrers_total)} />
          <Stat label="Referral points (30d)" value={fmtNum(stats.referral_points_30d)} />
          <Stat label="Trade points (30d)" value={fmtNum(stats.trade_points_30d)} />
          <Stat label="Referral points (all time)" value={fmtNum(stats.referral_points_total)} />
        </StatGrid>
      )}
    </div>
  );
}

// ── Top referrers leaderboard (admin only: wallet addresses are shown) ───────
function TopReferrersCard({ flash }) {
  const [rows, setRows] = useState(null);
  const [limit, setLimit] = useState(25);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const res = await getRewardsTopReferrers({ limit });
      setRows(listOf(res, ["referrers", "leaderboard", "rows", "items"]));
    } catch (err) {
      flash("error", apiError(err, "Failed to load top referrers"));
      setRows([]);
    }
  }, [flash, limit]);
  useEffect(() => { load(); }, [load]);

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <div>
          <h5 style={{ margin: 0 }}>Top referrers</h5>
          <div style={{ color: "var(--bl-muted, #8b93a7)", fontSize: 12 }}>By lifetime referral points. Internal view, do not share outside the team.</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <select style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid var(--bl-border, #2a2f3a)", background: "var(--bl-bg, #11141a)", color: "var(--bl-text, #cdd3df)" }} value={limit} onChange={(e) => setLimit(Number(e.target.value))} aria-label="How many referrers">
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>Top {n}</option>)}
          </select>
          <button style={btnGhost} onClick={load}>Refresh</button>
        </div>
      </div>
      {rows === null ? <Spinner /> : rows.length === 0 ? <Muted>No referrers yet.</Muted> : (
        <Table head={["#", "Wallet", "Referees", "Active", "L1 points", "L2 points", "L3 points", "Total points"]}>
          {rows.map((r, i) => (
            <tr key={r.wallet}>
              <td style={td}>{i + 1}</td>
              <td style={{ ...td, fontFamily: "monospace" }} title={r.wallet}>{shortAddr(r.wallet)}</td>
              <td style={td}>{fmtNum(r.referees)}</td>
              <td style={td}>{fmtNum(r.active_referees)}</td>
              <td style={td}>{fmtNum(r.points_l1)}</td>
              <td style={td}>{fmtNum(r.points_l2)}</td>
              <td style={td}>{fmtNum(r.points_l3)}</td>
              <td style={{ ...td, fontWeight: 700 }}>{fmtNum(r.points_total)}</td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

// ── Referrals tab (KPIs, leaderboard, who referred who) ──────────────────────
export function ReferralsTab({ flash }) {
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({ page: 1, total_pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setRows(null);
    try {
      const res = await getRewardsReferrals({ search: query || undefined, page, page_size: 25 });
      setRows(res?.referrals || []);
      setMeta(pageMetaOf(res));
    } catch (err) {
      flash("error", apiError(err, "Failed to load referrals"));
      setRows([]);
    }
  }, [flash, page, query]);
  useEffect(() => { load(); }, [load]);

  const submit = (e) => { e.preventDefault(); setPage(1); setQuery(search.trim().toLowerCase()); };

  return (
    <div>
      <ReferralStatsStrip flash={flash} />
      <TopReferrersCard flash={flash} />
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
          <h5 style={{ margin: 0 }}>Who referred who</h5>
          <SearchBar value={search} onChange={setSearch} onSubmit={submit} onRefresh={load} placeholder="Search referrer or referee…" />
        </div>
        {rows === null ? <Spinner /> : rows.length === 0 ? (
          <Muted>No referrals found.</Muted>
        ) : (
          <Table head={["Referrer (L1)", "Referee", "L2", "L3", "Since", "Until", "Active"]}>
            {rows.map((r) => (
              <tr key={r.referee_wallet}>
                <td style={td} title={r.referrer_wallet}>{shortAddr(r.referrer_wallet)}</td>
                <td style={td} title={r.referee_wallet}>{shortAddr(r.referee_wallet)}</td>
                <td style={td} title={r.l2_referrer_wallet || ""}>{shortAddr(r.l2_referrer_wallet)}</td>
                <td style={td} title={r.l3_referrer_wallet || ""}>{shortAddr(r.l3_referrer_wallet)}</td>
                <td style={td}>{fmtDate(r.start_at)}</td>
                <td style={td}>{fmtDate(r.end_at)}</td>
                <td style={td}>{yesNo(r.active)}</td>
              </tr>
            ))}
          </Table>
        )}
        <Pagination page={meta.page} totalPages={meta.total_pages} total={meta.total} onPage={setPage} />
      </div>
    </div>
  );
}
